import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ApiError, apiFetch } from '../api';
import { useToast } from '../toast';
import { AuthLayout } from '../components/AuthLayout';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_ALLOWED_RE = /^[A-Za-z0-9_]*$/;

const messages = {
  default: '喵~ 忘记密码了吗？',
  typing: '喵喵~ 认真填写哦！',
  password: '喵... 新密码要记住了！',
  code: '喵？验证码在邮箱里！',
  empty: '喵？还没填完呢~',
  success: '喵呜~ 重置成功！',
  peeking: '喵！我没偷看！',
  notPeeking: '喵~ 我什么都没看到！'
};

export function ResetPasswordPage() {
  const location = useLocation();
  const nav = useNavigate();
  const toast = useToast();

  const qs = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const next = useMemo(() => qs.get('next') || '/login', [qs]);
  const initialEmail = useMemo(() => String(qs.get('email') || ''), [qs]);

  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [emailExists, setEmailExists] = useState<boolean | null>(null);
  const [emailCheckLoading, setEmailCheckLoading] = useState(false);

  // Cat State
  const [catMessage, setCatMessage] = useState(messages.default);
  const [isPeeking, setIsPeeking] = useState(false);
  const [isCoveringEyes, setIsCoveringEyes] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = window.setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearInterval(t);
  }, [cooldown]);

  const normalizedEmail = email.trim();
  const emailValid = normalizedEmail.length > 0 && EMAIL_RE.test(normalizedEmail);

  const friendlyError = (err: unknown) => {
    if (!(err instanceof ApiError)) return '重置失败，请稍后重试';
    const code = (err.body as any)?.error;
    if (code === 'USER_NOT_FOUND') return '该邮箱未注册';
    if (code === 'CODE_NOT_SENT') return '请先发送验证码';
    if (code === 'CODE_EXPIRED') return '验证码已过期，请重新发送';
    if (code === 'CODE_INVALID') return '验证码不正确';
    if (code === 'INVALID_PASSWORD_FORMAT') return '密码仅支持字母/数字/下划线（至少 8 位）';
    if (code === 'WEAK_PASSWORD') return '密码强度不足（至少 8 位）';
    if (code === 'INVALID_EMAIL') return '邮箱格式不正确';
    if (code === 'TOO_MANY_REQUESTS') return '操作过于频繁，请稍后再试';
    return '重置失败，请检查验证码与邮箱是否正确';
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailValid) {
        toast.error('邮箱格式不正确');
        return;
    }
    if (emailExists === false) {
        toast.error('该邮箱未注册');
        return;
    }
    if (!code) {
        toast.error('请输入验证码');
        return;
    }
    if (!newPassword || newPassword.length < 8) {
        toast.error('请设置新密码（至少 8 位）');
        return;
    }

    setLoading(true);
    setCatMessage('喵... 正在重置密码...');
    
    try {
      const res = await apiFetch<{ ok?: boolean; error?: string }>('/api/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ email: normalizedEmail, code: code.trim(), newPassword })
      });
      if (res?.ok !== true) throw new ApiError(400, res);
      setCatMessage(messages.success);
      toast.success('密码已重置，请登录');
      nav(`/login?next=${encodeURIComponent(next)}`, { replace: true });
    } catch (e) {
      setCatMessage('喵呜... 重置失败了...');
      const msg = friendlyError(e);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    const newShowPassword = !showPassword;
    setShowPassword(newShowPassword);
    
    if (newShowPassword) {
      setIsCoveringEyes(true);
      setCatMessage(messages.peeking);
    } else {
      setIsCoveringEyes(false);
      setIsPeeking(false);
      setCatMessage(messages.notPeeking);
      setTimeout(() => {
        if (!isCoveringEyes) {
           setCatMessage(messages.default);
        }
      }, 1500);
    }
  };

  return (
    <AuthLayout catMessage={catMessage} isPeeking={isPeeking} isCoveringEyes={isCoveringEyes}>
      <h1 className="login-title">喵喵<span>重置密码</span></h1>
      <form onSubmit={handleReset}>
        <div className="form-group">
          <label>邮箱</label>
          <div style={{position: 'relative'}}>
            <input
                type="email"
                value={email}
                onChange={(e) => {
                    setEmail(e.target.value.replace(/\s+/g, '').slice(0, 254));
                    setEmailExists(null);
                }}
                onFocus={() => setCatMessage(messages.typing)}
                onBlur={async () => {
                    if (!isCoveringEyes) setCatMessage(messages.default);
                    
                    setErr(null);
                    setEmailExists(null);
                    if (!emailValid) return;
                    setEmailCheckLoading(true);
                    try {
                      const res = await apiFetch<{ ok: boolean; exists: boolean }>('/api/auth/check-email', {
                        method: 'POST',
                        body: JSON.stringify({ email: normalizedEmail })
                      });
                      setEmailExists(res.exists);
                      if (!res.exists) toast.error('该邮箱未注册');
                    } catch {
                      setEmailExists(null);
                    } finally {
                      setEmailCheckLoading(false);
                    }
                }}
                placeholder="name@example.com"
                autoComplete="email"
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                style={{paddingRight: '40px'}}
            />
            <span className="input-icon">📧</span>
          </div>
          <div style={{marginTop: 4, fontSize: 12, color: 'var(--cat-warm-brown)', minHeight: '18px'}}>
             {email.length > 0 && !emailValid ? '格式不正确' : emailExists === false ? '该邮箱未注册' : emailCheckLoading ? '检查中...' : ''}
          </div>
        </div>

        <div className="form-group">
            <label>验证码</label>
            <div style={{display: 'flex', gap: '10px'}}>
                <div style={{position: 'relative', flex: 1}}>
                    <input
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, '').slice(0, 6))}
                        onFocus={() => setCatMessage(messages.code)}
                        onBlur={() => !isCoveringEyes && setCatMessage(messages.default)}
                        placeholder="6 位数字"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                    />
                    <span className="input-icon" style={{right: '10px'}}>🔢</span>
                </div>
                <button
                    type="button"
                    className="login-btn"
                    style={{width: 'auto', marginTop: 0, padding: '0 15px', fontSize: '14px', whiteSpace: 'nowrap'}}
                    disabled={
                        loading ||
                        cooldown > 0 ||
                        !emailValid ||
                        emailExists === false
                    }
                    onClick={async (e) => {
                        e.preventDefault();
                        if (!emailValid) {
                            toast.error('邮箱格式不正确');
                            return;
                        }
                        if (emailExists === false) {
                            toast.error('该邮箱未注册');
                            return;
                        }
                        
                        try {
                            await apiFetch<{ ok: boolean }>('/api/auth/send-code', {
                                method: 'POST',
                                body: JSON.stringify({ email: normalizedEmail })
                            });
                            setCooldown(60);
                            toast.success('验证码已发送');
                            setCatMessage('喵！验证码发过去了！');
                        } catch (e) {
                            const msg = friendlyError(e);
                            toast.error(msg);
                        }
                    }}
                >
                    {cooldown > 0 ? `${cooldown}s` : '发送验证码'}
                </button>
            </div>
        </div>

        <div className="form-group">
          <label>新密码</label>
          <input
            type={showPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => {
                const v = e.target.value;
                const cleaned = PASSWORD_ALLOWED_RE.test(v) ? v : v.replace(/[^A-Za-z0-9_]/g, '');
                setNewPassword(cleaned.slice(0, 64));
            }}
            onFocus={() => setCatMessage(messages.password)}
            onBlur={() => !isCoveringEyes && setCatMessage(messages.default)}
            placeholder="8-64 位，仅字母/数字/下划线"
            autoComplete="new-password"
            minLength={8}
            maxLength={64}
          />
          <span 
            className="toggle-password"
            onClick={togglePasswordVisibility}
            onMouseEnter={() => !showPassword && setIsPeeking(true)}
            onMouseLeave={() => setIsPeeking(false)}
          >
            {showPassword ? '🙈' : '👁️'}
          </span>
          <span className="input-icon">🔒</span>
        </div>
        
        <button type="submit" className="login-btn" disabled={loading || !emailValid || emailExists === false}>
          {loading ? '重置中...' : '重置密码'}
        </button>
      </form>

      <div className="extra-links" style={{justifyContent: 'center'}}>
        <Link to={`/login?next=${encodeURIComponent(next)}`}>返回登录</Link>
      </div>
      
      <div style={{textAlign: 'center', marginTop: '20px'}}>
         <Link to="/" style={{color: 'var(--cat-warm-brown)', fontSize: '14px', textDecoration: 'none'}}>返回首页</Link>
      </div>
    </AuthLayout>
  );
  
  // Helper for setErr since I used toast directly mostly but onBlur used setErr in logic copy-paste
  function setErr(msg: string | null) {
      if (msg) toast.error(msg);
  }
}
